/**
 * `/v1/products/{id}/links` — the create body, the patch body, and the reorder body.
 *
 * These replace `links.ts`'s `parseCreate` / `parseGrain` / `parseHreflang` /
 * `parseInstant` family. **Replace, not wrap** (R7, §1.1a): a wrapper would be the
 * two-types outcome wearing one type's name, and the grep that closes it is that no
 * `parse*` function survives in the migrated route.
 *
 * ## Every message here is copy that already shipped
 *
 * `links.ts` is one of the two files carrying this product's safety copy, and its refusal
 * `detail` strings are pinned by the suite (c29's rule: user-facing copy is a claim about
 * what the system does, and nothing typechecks it). **Zod's default messages are nobody's
 * copy.** So each message below is transcribed character for character from the parser it
 * replaces, and a test going red on one of these is the migration being wrong rather than
 * the test being stale.
 *
 * ## Why the fields are `preprocess` pipes rather than plain `z.string()`
 *
 * Because the route they replace read every field through one of two coercions, and the
 * two are **not** the same rule:
 *
 *   - `optionalString` — anything that is not a non-empty string becomes `null`. So
 *     `mediaType: 123` is accepted by the shipped wire and stored as null. A
 *     `z.string()` here would answer 400 where the shipped API answers 201, which is a
 *     paid surface moving under a refactor that is not allowed to move it.
 *   - the **grain** qualifiers — `undefined`, `null` and `""` mean *"not defined at this
 *     level"*, and anything else must be a string in GS1 charset X. `grain.cpv: 123` is
 *     therefore **refused**, where `mediaType: 123` is ignored.
 *
 * Those two behaviours were three lines apart in the route and read as one rule. They are
 * spelled apart here so the difference is visible rather than inherited.
 *
 * ## Field order is refusal precedence, and it is load-bearing
 *
 * `refuseFromSchema` puts `issues[0]` on the wire, and zod reports object issues in the
 * order the shape declares its keys. The route checked linkType → href → mediaType →
 * grain → hreflang → validFrom → validTo, *"cheapest and most local first, so a request
 * that is wrong in two ways is told about the one it can fix"*. The declaration order
 * below is that order. Reordering these keys changes which refusal a doubly-malformed
 * request receives.
 */
import { z } from "zod";
import { CHARSET_X, CURIE, HTTP_URL, LANGUAGE_TAG, MEDIA_TYPE } from "./patterns.js";
const LINK_TYPE_MESSAGE = "linkType must be a CURIE such as gs1:pip. GET /v1/link-types lists every type " +
    "this organisation may use.";
const HREF_MESSAGE = "href must be an http:// or https:// URL.";
const MEDIA_TYPE_MESSAGE = "mediaType must be a media type such as text/html.";
const grainMessage = (key) => `grain.${key} must be 1–20 characters of GS1 charset X. It is matched against a ` +
    `scanned value exactly and case-sensitively, so it is stored exactly as sent.`;
const HREFLANG_ARRAY_MESSAGE = "hreflang must be an array of BCP 47 tags.";
const HREFLANG_EMPTY_MESSAGE = "hreflang must name at least one language, or be omitted entirely.";
const instantMessage = (key) => `${key} must be an ISO 8601 instant.`;
/** Pure, and exported because the op re-checks it against the row a PATCH would produce. */
export const VALIDITY_ORDER_MESSAGE = "validTo must be after validFrom.";
export const REORDER_MESSAGE = "linkIds must be an array of link ids, in the order you want.";
/** The route's `optionalString`, verbatim: a non-empty string, or null. */
const toOptionalString = (value) => typeof value === "string" && value !== "" ? value : null;
/**
 * The grain qualifiers' coercion, which is **not** {@link toOptionalString}.
 *
 * Absent, `null` and `""` all mean "not defined at this level". Everything else is a
 * value the caller meant, and is held to charset X — including a number, which the
 * route refused and which `optionalString` would have silently swallowed.
 */
const toAbsentOrValue = (value) => value === undefined || value === null || value === "" ? null : value;
const grainQualifier = (key) => z.preprocess(toAbsentOrValue, z.string(grainMessage(key)).regex(CHARSET_X, grainMessage(key)).nullable());
/**
 * The grain, as three nullable qualifiers.
 *
 * A non-object `grain` becomes `{}` rather than a refusal, which is what the route did:
 * `typeof raw === "object" && raw !== null ? raw : {}`. Unknown keys inside it are
 * stripped by `z.object`, matching the route's read of exactly three names.
 */
const grainSchema = z.preprocess((raw) => (typeof raw === "object" && raw !== null ? raw : {}), z.object({
    cpv: grainQualifier("cpv"),
    lot: grainQualifier("lot"),
    serial: grainQualifier("serial"),
}));
/**
 * Sorted and de-duplicated — the canonical form, produced here rather than in the op.
 *
 * `links_default_slot_key` indexes the array directly and array equality in Postgres is
 * ordered, so `{en,fr}` and `{fr,en}` are different keys to the index while being the
 * same content-negotiation variant to the merge. Canonicalising is what makes the index's
 * key mean what §3.5 means.
 *
 * It sits in the schema and not the op deliberately, and it is the one normalisation in
 * this file: splitting a field's validation from its canonical form across two layers is
 * how the two stop agreeing, and the SPA importing this schema gets the same answer the
 * server will store. `products.ts`'s *"nothing here is normalised"* is about `q`, where
 * case-sensitivity is a decision about the **query**; this is a decision about the
 * **value**, and it has an index behind it.
 */
const canonicalHreflang = (tags) => [...new Set(tags)].sort();
/**
 * `hreflang` — absent or null means no language stated; an **empty array does not**.
 *
 * `entryLinkSchema` requires hreflang to be absent or non-empty and `links_hreflang_check`
 * says the same, so `[]` is refused with its own sentence rather than quietly treated as
 * "no preference". The tag message names the offending tag, which is why this is a
 * `superRefine` walking the array rather than `z.array(z.string().regex(...))` — zod's
 * per-element message could not say which one.
 */
const hreflangSchema = z.preprocess((value) => (value === undefined || value === null ? null : value), z
    .unknown()
    .superRefine((value, ctx) => {
    if (value === null)
        return;
    if (!Array.isArray(value) || value.some((tag) => typeof tag !== "string")) {
        ctx.addIssue({ code: "custom", message: HREFLANG_ARRAY_MESSAGE });
        return;
    }
    if (value.length === 0) {
        ctx.addIssue({ code: "custom", message: HREFLANG_EMPTY_MESSAGE });
        return;
    }
    const bad = value.find((tag) => !LANGUAGE_TAG.test(tag));
    if (bad !== undefined) {
        ctx.addIssue({
            code: "custom",
            message: `${JSON.stringify(bad)} is not a BCP 47 tag.`,
        });
    }
})
    .transform((value) => (value === null ? null : canonicalHreflang(value))));
/**
 * An ISO 8601 instant, or null. Absent, null and `""` all mean "no bound".
 *
 * `Date.parse` rather than a regex, exactly as the route had it. It is looser than ISO
 * 8601 — `Date.parse("2026")` succeeds — and it is what shipped; tightening it here would
 * refuse bodies the API currently accepts.
 */
const instantSchema = (key) => z.preprocess((value) => (value === undefined || value === null || value === "" ? null : value), z
    .unknown()
    .superRefine((value, ctx) => {
    if (value === null)
        return;
    if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
        ctx.addIssue({ code: "custom", message: instantMessage(key) });
    }
})
    .transform((value) => (value === null ? null : value)));
/**
 * The tier a batch-level link is included from — `screens.md` §5's trial gate. Creation
 * only: an existing batch-level link keeps resolving on any plan (Q-P4).
 */
export const BATCH_LINK_TIER = "growth";
/** A lot or a serial makes a link batch-level. A `cpv` alone is a variant, on every tier. */
export function isBatchGrain(grain) {
    return (grain.lot ?? null) !== null || (grain.serial ?? null) !== null;
}
/**
 * `POST /v1/products/{id}/links`.
 *
 * Not `.strict()`. The route this replaces reads the keys it wants and ignores the rest,
 * and a migration that starts refusing unknown fields is a breaking change to a paid
 * surface wearing the clothes of a tightening (`products.ts` carries the whole rule).
 *
 * **`isDefault` and `active` never refuse**, which looks like laxity and is the shipped
 * wire: `isDefault` is true only for the literal `true`, and `active` is false only for
 * the literal `false`. So `active: "no"` creates an **active** link. That is worth reading
 * twice, and it is not this commit's to change — a schema tightening it would answer 400
 * where the API answers 201.
 */
export const createLinkSchema = z
    .object({
    linkType: z.preprocess(toOptionalString, z.string(LINK_TYPE_MESSAGE).regex(CURIE, LINK_TYPE_MESSAGE)),
    href: z.preprocess(toOptionalString, z.string(HREF_MESSAGE).regex(HTTP_URL, HREF_MESSAGE)),
    mediaType: z.preprocess(toOptionalString, z.string().regex(MEDIA_TYPE, MEDIA_TYPE_MESSAGE).nullable()),
    grain: grainSchema,
    hreflang: hreflangSchema,
    validFrom: instantSchema("validFrom"),
    validTo: instantSchema("validTo"),
    title: z.preprocess(toOptionalString, z.string().nullable()),
    isDefault: z.preprocess((value) => value === true, z.boolean()),
    active: z.preprocess((value) => value !== false, z.boolean()),
})
    .superRefine((input, ctx) => {
    // `validTo` is an EXCLUSIVE upper bound, so an equal pair is an empty window rather
    // than an instant one — a link that can never resolve. Mirrors
    // `links_validity_order_check`. The PATCH's version of this check cannot live here:
    // it compares against the row's stored values, which is a fact about the database.
    if (input.validFrom === null || input.validTo === null)
        return;
    if (Date.parse(input.validTo) > Date.parse(input.validFrom))
        return;
    ctx.addIssue({ code: "custom", message: VALIDITY_ORDER_MESSAGE });
})
    .describe("POST /v1/products/{id}/links");
/**
 * `PATCH /v1/products/{id}/links/{linkId}`.
 *
 * Every field is optional and **absent means unchanged**, which is the route's
 * `"href" in body` read expressed as a type. `.optional()` sits outside each preprocess
 * pipe on purpose: `ZodOptional` short-circuits on `undefined` without running the pipe,
 * so an omitted key stays omitted instead of being coerced to null by a preprocessor that
 * cannot tell the two apart.
 *
 * **The grain and the link type are absent from this schema, not optional in it.** They
 * are what a link *is* — the pair deciding which scans it answers and what relation it
 * declares — and changing either is indistinguishable from deleting one link and creating
 * another, except that the audit trail says something happened to the first one.
 */
export const updateLinkSchema = z
    .object({
    href: z
        .preprocess(toOptionalString, z.string(HREF_MESSAGE).regex(HTTP_URL, HREF_MESSAGE))
        .optional(),
    mediaType: z
        .preprocess(toOptionalString, z.string().regex(MEDIA_TYPE, MEDIA_TYPE_MESSAGE).nullable())
        .optional(),
    hreflang: hreflangSchema.optional(),
    validFrom: instantSchema("validFrom").optional(),
    validTo: instantSchema("validTo").optional(),
    title: z.preprocess(toOptionalString, z.string().nullable()).optional(),
    isDefault: z.preprocess((value) => value === true, z.boolean()).optional(),
})
    .describe("PATCH /v1/products/{id}/links/{linkId}");
/**
 * `POST /v1/products/{id}/links/reorder`.
 *
 * **The whole set, or none of it.** Ids are not shape-checked beyond being strings: the
 * op compares the sent list against the GTIN's actual set, and a malformed id is caught
 * there with a sentence that names it rather than here with one that does not.
 */
export const reorderLinksSchema = z
    .object({
    linkIds: z.preprocess((value) => value, z
        .unknown()
        .superRefine((value, ctx) => {
        if (Array.isArray(value) && value.every((id) => typeof id === "string"))
            return;
        ctx.addIssue({ code: "custom", message: REORDER_MESSAGE });
    })
        .transform((value) => (Array.isArray(value) ? value : []))),
})
    .describe("POST /v1/products/{id}/links/reorder");
//# sourceMappingURL=links.js.map